import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ReferralExpiryAlert } from "@/components/ReferralExpiry";

const TONES = {
  pending: { background: "rgba(201,162,75,0.16)", color: "#8a6a1f", border: "1px solid rgba(201,162,75,0.45)" },
  credited: { background: "rgba(34,139,84,0.12)", color: "#1f7a4a", border: "1px solid rgba(34,139,84,0.4)" },
  expired: { background: "var(--bg-soft)", color: "var(--text-muted)", border: "1px solid var(--border)" },
  linked: { background: "rgba(59,110,165,0.1)", color: "#2f5f8f", border: "1px solid rgba(59,110,165,0.35)" },
};

const Badge = ({ tone, children, testid }) => (
  <span className="inline-flex items-center text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full whitespace-nowrap"
        style={TONES[tone] || TONES.linked} data-testid={testid}>{children}</span>
);

export function useReferralSummary(customerId, refreshKey) {
  const [s, setS] = useState(null);
  useEffect(() => {
    if (!customerId) return;
    let live = true;
    setS(null);
    api.get(`/customers/${customerId}/referral-summary`).then(({ data }) => live && setS(data)).catch(() => live && setS(null));
    return () => { live = false; };
  }, [customerId, refreshKey]);
  return s;
}

// Referee badge (one, by status) + Referrer badges (one per non-zero status count)
export function ReferralBadges({ s, className = "" }) {
  if (!s) return null;
  const rb = s.referee_badge || s.referee_status;
  const referrer = [["credited", s.referrer_credited], ["pending", s.referrer_pending], ["expired", s.referrer_expired]]
    .filter(([, n]) => n > 0);
  const linked = (s.referred_members || []).filter((m) => m.status === "linked").length;
  if (!rb && !referrer.length && !linked) return null;
  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`} data-testid="referral-badges">
      {rb && <Badge tone={rb} testid={`badge-referee-${rb}`}>Referee{rb !== "linked" ? ` · ${rb}` : ""}</Badge>}
      {referrer.map(([k, n]) => <Badge key={k} tone={k} testid={`badge-referrer-${k}`}>Referrer · {n} {k}</Badge>)}
      {linked > 0 && <Badge tone="linked" testid="badge-referrer-linked">Referrer · {linked} linked</Badge>}
    </div>
  );
}

const Row = ({ label, value, testid, mono }) => (
  <>
    <dt style={{ color: "var(--text-muted)" }}>{label}</dt>
    <dd className={mono ? "font-mono" : ""} data-testid={testid}>{value}</dd>
  </>
);

// Read-only referral status for staff/admin member cards; pass `summary` to reuse an existing fetch
export default function MemberReferralInfo({ customerId, refreshKey, summary, hideBadges }) {
  const fetched = useReferralSummary(summary === undefined ? customerId : null, refreshKey);
  const s = summary === undefined ? fetched : summary;
  if (!s) return null;
  const members = s.referred_members || [];
  return (
    <div className="mt-3 space-y-2" data-testid="member-referral-info">
      <ReferralExpiryAlert expiresAt={s.referee_expires_at} testid="referee-expiry-alert"
        text={(d, date) => `Referral expires in ${d} (${date}). A first purchase is needed before then to credit both members.`} />
      <ReferralExpiryAlert expiresAt={s.referrer_soonest?.expires_at} testid="referrer-expiry-alert"
        text={(d, date) => `${s.referrer_soonest?.name || "A referred member"} (${s.referrer_soonest?.customer_id}) has ${d} left (${date}) to make a first purchase, or this member's referrer points expire.`} />
      {!hideBadges && <ReferralBadges s={s} />}
      <dl className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs max-w-sm">
        <Row label="Referred By ID" mono testid="referred-by-id" value={s.referred_by_id || "N/A"} />
        <Row label="Referred By Name" testid="referred-by-name" value={s.referred_by_id ? (s.referred_by_name || "N/A") : "N/A"} />
        {members.map((m, i) => (
          <React.Fragment key={m.customer_id}>
            <Row label="Referred Member ID" mono testid={`referred-member-id-${i}`} value={m.customer_id} />
            <Row label="Referred Member Name" testid={`referred-member-name-${i}`} value={m.name || "N/A"} />
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
}
